/**
 * "I need something" — the service-request path.
 *
 * Two steps: pick an offering from the catalog, then answer the questions that
 * offering declares. The parameter list is data from Ivanti, so the form is
 * generated (see `offering-parameter-fields.tsx`) and the required-field check
 * skips anything a `depends_on` rule is currently hiding.
 *
 * The subject defaults to the offering's own name — the contract requires one
 * on every ticket, and making an employee invent a title for "New monitor" is
 * pointless friction. They can still edit it.
 *
 * Files are offered on every offering, not only the ones whose author happened
 * to declare an `ATTACHMENT` parameter. Whether a user can send the quote, the
 * signed form or the photo of the broken dock is otherwise decided by a catalog
 * author in Ivanti who was not thinking about that case, and being told "attach
 * it to the ticket afterwards" is the friction this portal exists to remove.
 * The two sets do not conflict on the wire: `TicketCreateRequest.attachment_ids`
 * is top-level, per-parameter files ride on their own answer, and the server's
 * `_all_attachment_ids` merges and de-duplicates both before filing. An
 * offering that *requires* a document still does so through its own required
 * ATTACHMENT parameter — this adds a door, it does not remove a lock.
 *
 * No press-again prompt here. Most of the catalog (a monitor, a licence, an
 * account) has nothing to attach, and the offerings that genuinely need a
 * document already say so with a required parameter that blocks properly.
 */

import { Alert, Button, cn, Field, Form, Typography } from "@norican/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { AttachmentUploader, usePasteToAttach } from "@/components/attachments";
import {
  ErrorState,
  FieldHighlightLegend,
  FormAnnouncer,
  FormReview,
  type FormReviewRow,
  LoadingState,
  REVIEW_HIDES_FORM,
  SectionCard,
  useFormAnnouncer,
  useFormCheckMessage,
  useSubmitLabel,
} from "@/components/common";
import { FilingAsLine } from "@/components/layout";
import { answeredByCase, type StarterPrefillAnswer, withStarterPeople } from "@/features/starters";
import { useLanguage, useT } from "@/i18n";
import {
  checkOfferingAnswer,
  type FormCheckKey,
  needsReviewStep,
  stillNeededCount,
  todayIso,
} from "@/lib/form-checks";
import { logger } from "@/lib/logger";
import { revealFieldSoon, revealFirstProblemSoon } from "@/lib/reveal-first-problem";

import type {
  OfferingParameter,
  RequestOfferingSummary,
  TicketOfferingAnswer,
  TranslatableText,
} from "../api/contract";
import { useRequestOffering, useTranslateTo } from "../api/queries";
import { bluestarRolesParameterId } from "../lib/bluestar-areas";
import { applyPrefill, withRequiredIds } from "../lib/offering-prefill";
import { displayText } from "../lib/translatable";
import { useManagerAutofill } from "../lib/use-manager-autofill";
import { type ReturnAfterCreate, useTicketSubmit } from "../lib/use-ticket-submit";
import { validateDescription, validateSubject, type ValidationKey } from "../lib/validation";
import {
  answerFor,
  type AnswerMap,
  isAnswerMissing,
  isParameterVisible,
  OfferingParameterField,
  seedLookupAnswers,
  seedLookupDisplayNames,
  withCurrentRequirement,
} from "./offering-parameter-fields";
import { OfferingPicker } from "./offering-picker";
import { useOfferingReviewRows } from "./offering-review";
import { TicketBasicsFields } from "./ticket-basics-fields";
import { TranslatableTextView } from "./translatable-text";

/**
 * A form opened from a new starter case (2 Oct): the offering to open, and the
 * answers and subject the case supplies. The person still reviews and submits.
 */
export interface StarterFormPrefill {
  offeringId: string;
  subject: string;
  values: StarterPrefillAnswer[];
  /** The "anything else" text to open with (an access item being edited, 5 Oct). */
  description?: string;
  /**
   * What a prefilled field says it came from (14 Oct): the access front door
   * passes its case's wording; the new starter flow keeps its own default.
   */
  prefilledNote?: string;
}

/** One answered form, handed back instead of filed (`collect` mode, 5 Oct). */
export interface CollectedRequest {
  offeringId: string;
  subject: string;
  description: string;
  answers: TicketOfferingAnswer[];
}

/**
 * The user access front door (5 Oct) uses this same form for each checklist
 * item, but files them together at the end: the form validates as always and
 * hands the answers back. Fields the app answers itself later (a new hire's
 * person field) are left out, and files are not offered — a held item cannot
 * carry an upload until it is filed.
 */
export interface CollectMode {
  submitLabel: string;
  onCollect: (request: CollectedRequest) => void;
  onCancel: () => void;
  cancelLabel?: string;
  hiddenParameterIds?: readonly string[];
  /**
   * Fields the page needs answered though the offering leaves them optional
   * (28 Oct): the access wizard's 365FO "Company / entity". Asterisk, yellow
   * tint and held submit, as for the offering's own required fields.
   */
  requiredParameterIds?: readonly string[];
  pending?: boolean;
  error?: unknown;
  /**
   * One field whose answer the page follows as it changes (5 Oct): the access
   * item page shows the approvers for the company chosen on the form.
   */
  watchParameterId?: string | null;
  onWatchChange?: (answer: { value: string | null; valueIds: string[] }) => void;
  /** A second followed field (14 Oct): CRM's area, beside the company. */
  watchAlsoParameterId?: string | null;
  onWatchAlsoChange?: (answer: { value: string | null; valueIds: string[] }) => void;
  /**
   * False: "Roles or access needed" stays a text box when the system is
   * Bluestar (8 Oct) — "Open and complete", whose own picker is above it.
   */
  bluestarAreas?: boolean;
}

export interface ServiceRequestFormProps {
  /** Open straight on this offering, prefilled, instead of the catalog. */
  starterPrefill?: StarterFormPrefill | null;
  /** Hand the answers back instead of filing them. */
  collect?: CollectMode | null;
  /** Once filed, go here instead of to the new ticket (a case page, 5 Oct). */
  returnTo?: ReturnAfterCreate | null;
  /**
   * Open straight on this offering's form, not prefilled (19 Oct): the
   * email intake's form-link reply, `/tickets/new?offering=<id>`. "Change"
   * still goes back to the catalog.
   */
  initialOfferingId?: string | null;
}

export function ServiceRequestForm({
  starterPrefill = null,
  collect = null,
  returnTo = null,
  initialOfferingId = null,
}: ServiceRequestFormProps) {
  const { t } = useT("tickets");
  const translateTo = useTranslateTo();
  const [selectedId, setSelectedId] = useState<string | null>(
    starterPrefill?.offeringId ?? initialOfferingId,
  );
  const offering = useRequestOffering(selectedId, translateTo);

  if (selectedId === null) {
    return (
      <OfferingPicker
        onSelect={(next: RequestOfferingSummary) => {
          setSelectedId(next.offering_id);
        }}
      />
    );
  }

  if (offering.isPending) {
    return <LoadingState />;
  }

  if (offering.isError || offering.data === undefined) {
    return (
      <ErrorState
        error={offering.error}
        onRetry={() => void offering.refetch()}
        actions={
          <Button variant="outline" tone="neutral" onClick={() => setSelectedId(null)}>
            {t("create.offering.change")}
          </Button>
        }
      />
    );
  }

  return (
    <OfferingAnswerForm
      // Remounting on offering change resets every answer — carrying answers
      // from one offering's parameters to another's would be nonsense.
      key={offering.data.offering_id}
      offeringId={offering.data.offering_id}
      offeringName={displayText(offering.data.name)}
      onChangeOffering={() => setSelectedId(null)}
      parameters={offering.data.parameters ?? []}
      description={offering.data.description ?? null}
      requiresApproval={offering.data.requires_approval ?? false}
      starterPrefill={
        starterPrefill !== null && starterPrefill.offeringId === offering.data.offering_id
          ? starterPrefill
          : null
      }
      collect={collect}
      returnTo={returnTo}
    />
  );
}

interface OfferingAnswerFormProps {
  offeringId: string;
  offeringName: string;
  onChangeOffering: () => void;
  parameters: OfferingParameter[];
  description: TranslatableText | null;
  requiresApproval: boolean;
  starterPrefill: StarterFormPrefill | null;
  collect: CollectMode | null;
  returnTo: ReturnAfterCreate | null;
}

function OfferingAnswerForm({
  offeringId,
  offeringName,
  onChangeOffering,
  parameters: offeringParameters,
  description,
  requiresApproval,
  starterPrefill,
  collect,
  returnTo,
}: OfferingAnswerFormProps) {
  const { t } = useT("tickets");
  const { t: tCommon } = useT();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { submit, isPending: submitting, error: submitError } = useTicketSubmit(returnTo);
  const isPending = submitting || (collect?.pending ?? false);
  const shownError: unknown = collect === null ? submitError : (collect.error ?? null);

  // A form opened from a new starter case starts on the case's answers: its
  // people become the pickers' defaults (so they show by name), everything
  // else goes through the same never-invent check as the assistant's prefill.
  const [initial] = useState(() => {
    if (starterPrefill === null) {
      return {
        parameters: offeringParameters,
        answers: seedLookupAnswers(offeringParameters),
        prefilledIds: [] as string[],
      };
    }
    const withPeople = withStarterPeople(offeringParameters, starterPrefill.values);
    const applied = applyPrefill(withPeople, seedLookupAnswers(withPeople), starterPrefill.values);
    logger.debug("[starters] request form prefilled", offeringId, applied.prefilledIds.length);
    return { parameters: withPeople, ...applied };
  });
  const parameters = initial.parameters;
  // Answered by the case (5 Oct): still submitted with `visible`, and still
  // driving the questions that depend on it, but not asked again.
  const [caseAnswered] = useState(() =>
    answeredByCase(starterPrefill?.values, initial.prefilledIds),
  );
  const [prefilledIds, setPrefilledIds] = useState<ReadonlySet<string>>(
    () => new Set(initial.prefilledIds),
  );
  const initialSubject = starterPrefill?.subject ?? offeringName;
  const [subject, setSubject] = useState(initialSubject);
  const [requestDescription, setRequestDescription] = useState(starterPrefill?.description ?? "");
  const [answers, setAnswers] = useState<AnswerMap>(() => initial.answers);
  const [subjectError, setSubjectError] = useState<ValidationKey | null>(null);
  const [descriptionError, setDescriptionError] = useState<ValidationKey | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  /* Plain checks (7 Oct): shown from the Submit that found them until the
     field is changed, like a missing answer — the Form will not submit while
     a field shows an error. */
  const [problems, setProblems] = useState<Record<string, FormCheckKey>>({});
  /** Person lookups with a name typed but nobody picked from the list. */
  const [unpicked, setUnpicked] = useState<Record<string, boolean>>({});
  /** Picked colleagues' names, for the summary (it must not show a RecId). */
  const [employeeNames, setEmployeeNames] = useState<Record<string, string>>(() =>
    seedLookupDisplayNames(initial.parameters),
  );
  /** "Here's what IT will get" is on screen instead of the form. */
  const [reviewing, setReviewing] = useState(false);
  const { message: announcement, announce } = useFormAnnouncer();
  const submitLabel = useSubmitLabel();
  const checkMessage = useFormCheckMessage();
  const reviewRows = useOfferingReviewRows();

  /* On a phone the submit button is a screen away from the error summary and
     the first unanswered field: bring the problem into view when it appears. */
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (shownError !== null && shownError !== undefined) {
      revealFirstProblemSoon(() => formRef.current);
    }
  }, [shownError]);
  const clearMissing = (parameterId: string) => {
    setMissing((previous) =>
      previous.includes(parameterId) ? previous.filter((id) => id !== parameterId) : previous,
    );
    setProblems((previous) => {
      if (previous[parameterId] === undefined) {
        return previous;
      }
      const { [parameterId]: _cleared, ...rest } = previous;
      return rest;
    });
  };
  /* Kept apart from the per-parameter answers on purpose: these travel at the
     top level of the request, not on an answer, and folding them into a
     parameter that may not exist would be inventing one. */
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  /* Aimed at the general attachment field below, not at whichever `ATTACHMENT`
     parameter the offering happens to declare: that one is a specific ask ("the
     signed form"), this one is the catch-all. A paste landing inside the
     parameter's own field still goes there — it has its own boundary and claims
     the event first. */
  const paste = usePasteToAttach({ disabled: isPending });
  const [uploading, setUploading] = useState(false);

  // The watched fields' answers, reported on every change (and once at start).
  useWatchedAnswer(answers, collect?.watchParameterId ?? null, collect?.onWatchChange);
  useWatchedAnswer(answers, collect?.watchAlsoParameterId ?? null, collect?.onWatchAlsoChange);

  const hidden = new Set(collect?.hiddenParameterIds ?? []);
  const requiredIds = new Set(collect?.requiredParameterIds ?? []);
  const ordered = [...parameters]
    .filter((parameter) => !hidden.has(parameter.parameter_id))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const visible = ordered
    .filter((parameter) => isParameterVisible(parameter, answers))
    .map((parameter) => withRequiredIds(withCurrentRequirement(parameter, answers), requiredIds));
  const asked = visible.filter((parameter) => !caseAnswered.has(parameter.parameter_id));
  // 8 Oct: with the system Bluestar, "Roles or access needed" is its area picker.
  const bluestarRolesId =
    collect?.bluestarAreas === false ? null : bluestarRolesParameterId(parameters, answers);

  // The same never-clobber auto-fill the assistant's inline form uses: this
  // renders the identical parameter list, so a manager field paired with an
  // employee lookup has to behave the same way on both.
  const managerAutofill = useManagerAutofill(parameters, setAnswers);

  /* A subject the app wrote (the offering's own name, a case's subject) is
     not held to the five-character floor: only one the person typed is. */
  const checkSubject = (value: string): ValidationKey | null =>
    value.trim() !== "" && value.trim() === initialSubject.trim() ? null : validateSubject(value);

  // The count on the button (7 Oct): the questions on screen still tinted
  // "still needed", and the subject when it has been emptied.
  const stillNeeded = stillNeededCount([
    subject.trim() === "",
    ...asked.map((parameter) =>
      isAnswerMissing(parameter, answerFor(answers, parameter.parameter_id)),
    ),
  ]);
  const reviewFirst = needsReviewStep({
    form: "service-request",
    askedQuestions: asked.length,
    collect: collect !== null,
  });

  /**
   * Check everything; when something is missing or wrong, mark it, take the
   * person to the first one and say how many. Returns whether Submit is held.
   */
  const holdSubmit = (): boolean => {
    const nextSubjectError = checkSubject(subject);
    // Optional here: the offering's own parameters are the questions, and many
    // offerings already ask for a description of their own. Only the length
    // limit still applies when something is typed.
    const nextDescriptionError =
      requestDescription.trim() === "" ? null : validateDescription(requestDescription);
    const nextMissing = visible
      .filter((parameter) => isAnswerMissing(parameter, answerFor(answers, parameter.parameter_id)))
      .map((parameter) => parameter.parameter_id);
    const today = todayIso();
    const nextProblems: Record<string, FormCheckKey> = {};
    for (const parameter of asked) {
      if (nextMissing.includes(parameter.parameter_id)) {
        continue;
      }
      const problem = checkOfferingAnswer(parameter, answerFor(answers, parameter.parameter_id), {
        today,
        unpicked: unpicked[parameter.parameter_id] === true,
      });
      if (problem !== null) {
        nextProblems[parameter.parameter_id] = problem;
      }
    }

    setSubjectError(nextSubjectError);
    setDescriptionError(nextDescriptionError);
    setMissing(nextMissing);
    setProblems(nextProblems);

    const needed = nextMissing.length + (nextSubjectError === "subjectRequired" ? 1 : 0);
    const toFix =
      Object.keys(nextProblems).length +
      (nextSubjectError !== null && nextSubjectError !== "subjectRequired" ? 1 : 0) +
      (nextDescriptionError === null ? 0 : 1);
    if (needed + toFix === 0) {
      return false;
    }
    revealFirstProblemSoon(() => formRef.current);
    announce(needed, toFix);
    return true;
  };

  const handleSubmit = () => {
    if (holdSubmit()) {
      return;
    }
    // The longer forms show "Here's what IT will get" first (7 Oct).
    if (reviewFirst && !reviewing) {
      setReviewing(true);
      return;
    }
    send();
  };

  const backToForm = (fieldKey?: string) => {
    setReviewing(false);
    if (fieldKey !== undefined) {
      revealFieldSoon(() => formRef.current, fieldKey);
    }
  };

  const send = () => {
    const payloadAnswers: TicketOfferingAnswer[] = visible.map((parameter) => {
      const answer = answerFor(answers, parameter.parameter_id);
      // An ATTACHMENT parameter carries the files uploaded to the draft on
      // `attachment_ids`; every other type answers through `value`/`value_ids`.
      if (parameter.type === "ATTACHMENT") {
        return {
          parameter_id: parameter.parameter_id,
          attachment_ids: answer.attachmentIds ?? [],
        };
      }
      return {
        parameter_id: parameter.parameter_id,
        value: answer.value,
        value_ids: answer.valueIds,
        attachment_ids: [],
      };
    });

    if (collect !== null) {
      logger.debug("[tickets] request form collected", offeringId, payloadAnswers.length);
      collect.onCollect({
        offeringId,
        subject: subject.trim(),
        description: requestDescription.trim() || subject.trim(),
        answers: payloadAnswers,
      });
      return;
    }

    void submit({
      kind: "SERVICE_REQUEST",
      subject: subject.trim(),
      // The API requires a non-empty description on every ticket, so an empty
      // catch-all falls back to the person's own subject line rather than to
      // invented text.
      description: requestDescription.trim() || subject.trim(),
      body_lang: language,
      offering_id: offeringId,
      attachment_ids: attachmentIds,
      answers: payloadAnswers,
    });
  };

  return (
    <Form
      ref={formRef}
      // The summary hides the form rather than unmounting it: unmounting
      // would drop the uploads and every answer's local state.
      className={cn("flex flex-col gap-6", reviewing && REVIEW_HIDES_FORM)}
      onFormSubmit={handleSubmit}
      onPaste={paste.onPaste}
    >
      <SectionCard
        data-review-keep
        title={offeringName}
        actions={
          collect === null ? (
            <Button variant="ghost" tone="neutral" size="sm" onClick={onChangeOffering}>
              {t("create.offering.change")}
            </Button>
          ) : null
        }
      >
        <div className="flex flex-col gap-3">
          {description === null ? null : (
            <Typography as="div" variant="body-sm" tone="muted">
              <TranslatableTextView value={description} multiline />
            </Typography>
          )}
          {requiresApproval ? (
            <Alert tone="info">{t("create.offering.approvalNotice")}</Alert>
          ) : null}
        </div>
      </SectionCard>

      {shownError === null || shownError === undefined ? null : (
        <div data-form-problem data-review-keep>
          <ErrorState variant="inline" error={shownError} offerHelpdeskContact />
        </div>
      )}

      {reviewing ? (
        <FormReview
          rows={[
            {
              key: "subject",
              label: t("field.subject"),
              value: subject.trim(),
              onEdit: () => backToForm("subject"),
            },
            ...reviewRows(asked, answers, employeeNames, backToForm),
            ...(requestDescription.trim() === ""
              ? []
              : [
                  {
                    key: "description",
                    label: t("create.request.extraLabel"),
                    value: requestDescription.trim(),
                    onEdit: () => backToForm("description"),
                  } satisfies FormReviewRow,
                ]),
            ...(attachmentIds.length === 0
              ? []
              : [
                  {
                    key: "request-attachments",
                    label: t("create.request.attachLabel"),
                    value: tCommon("formCheck.review.files", { count: attachmentIds.length }),
                    onEdit: () => backToForm("request-attachments"),
                  } satisfies FormReviewRow,
                ]),
          ]}
          onBack={() => backToForm()}
          onSend={handleSubmit}
          sending={isPending}
          sendLabel={t("create.submitRequest")}
          footer={<FilingAsLine className="justify-end text-end" />}
        />
      ) : null}

      <FieldHighlightLegend />

      <TicketBasicsFields
        subject={subject}
        description={requestDescription}
        prefilledSubject={starterPrefill?.subject ?? offeringName}
        prefilledDescription={starterPrefill?.description ?? null}
        onSubjectChange={(value) => {
          setSubject(value);
          // The Form will not submit while a field shows an error: clear it
          // as soon as the value is valid again, as the incident form does.
          if (subjectError !== null && checkSubject(value) === null) {
            setSubjectError(null);
          }
        }}
        onDescriptionChange={(value) => {
          setRequestDescription(value);
          if (
            descriptionError !== null &&
            (value.trim() === "" || validateDescription(value) === null)
          ) {
            setDescriptionError(null);
          }
        }}
        subjectError={subjectError}
        descriptionError={descriptionError}
        descriptionLabel={t("create.request.extraLabel")}
        descriptionHint={null}
        descriptionRequired={false}
      />

      {asked.length === 0 ? null : (
        <SectionCard title={t("create.request.detailsTitle")}>
          <div className="flex flex-col gap-6">
            {asked.map((parameter) => (
              <OfferingParameterField
                key={parameter.parameter_id}
                parameter={parameter}
                answer={answerFor(answers, parameter.parameter_id)}
                checkOnLeave
                error={
                  missing.includes(parameter.parameter_id)
                    ? t("validation.fieldRequired")
                    : checkMessage(problems[parameter.parameter_id])
                }
                onTextPendingChange={(pending) =>
                  setUnpicked((previous) =>
                    previous[parameter.parameter_id] === pending
                      ? previous
                      : { ...previous, [parameter.parameter_id]: pending },
                  )
                }
                autoFilled={managerAutofill.isAutoFilled(parameter.parameter_id)}
                prefilled={prefilledIds.has(parameter.parameter_id)}
                prefilledNote={starterPrefill?.prefilledNote ?? t("starters.form.prefilledNote")}
                bluestarAreas={parameter.parameter_id === bluestarRolesId}
                onEmployeeSelect={(employee) => {
                  clearMissing(parameter.parameter_id);
                  if (employee !== null) {
                    setEmployeeNames((previous) => ({
                      ...previous,
                      [parameter.parameter_id]: employee.display_name,
                    }));
                  }
                  managerAutofill.fillFrom(parameter.parameter_id, employee, answers);
                }}
                onChange={(next) => {
                  // Cleared on change: the form will not submit while a field
                  // is marked invalid, so waiting for the next submit to clear
                  // it would leave the person stuck.
                  clearMissing(parameter.parameter_id);
                  if (prefilledIds.has(parameter.parameter_id)) {
                    setPrefilledIds((previous) => {
                      const rest = new Set(previous);
                      rest.delete(parameter.parameter_id);
                      return rest;
                    });
                  }
                  managerAutofill.noteEdit(parameter.parameter_id, next);
                  setAnswers((previous) => ({ ...previous, [parameter.parameter_id]: next }));
                }}
              />
            ))}
          </div>
        </SectionCard>
      )}

      {collect === null ? (
        <Field
          name="request-attachments"
          label={t("create.request.attachLabel")}
          hint={t("create.request.attachHint")}
          data-field-key="request-attachments"
        >
          <AttachmentUploader
            ref={paste.ref}
            id="request-attachments"
            onChange={setAttachmentIds}
            onPendingChange={setUploading}
            disabled={isPending}
          />
        </Field>
      ) : null}

      {/* Shared PCs: whose name this goes out under, where Submit is pressed. */}
      <FilingAsLine className="-mb-3 justify-end text-end" />

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          tone="neutral"
          onClick={() => (collect === null ? void navigate("/tickets") : collect.onCancel())}
        >
          {collect?.cancelLabel ?? tCommon("button.cancel")}
        </Button>
        <Button
          type="submit"
          loading={isPending || uploading}
          disabled={isPending || uploading}
          onClick={(event) => {
            // Checked here too, before the Form's own validation can swallow
            // the press without a word (7 Oct: never block silently).
            if (holdSubmit()) {
              event.preventDefault();
            }
          }}
        >
          {uploading
            ? t("attachment.uploading")
            : submitLabel(
                collect?.submitLabel ??
                  (reviewFirst ? tCommon("formCheck.review.next") : t("create.submitRequest")),
                stillNeeded,
              )}
        </Button>
      </div>
      <FormAnnouncer message={announcement} />
    </Form>
  );
}

/** Reports one field's answer to the page on every change, and once at start. */
function useWatchedAnswer(
  answers: Parameters<typeof answerFor>[0],
  watchId: string | null,
  onChange: ((answer: { value: string | null; valueIds: string[] }) => void) | undefined,
) {
  const watched = watchId === null ? null : answerFor(answers, watchId);
  const watchedValue = watched?.value ?? null;
  const watchedIds = (watched?.valueIds ?? []).join("\u0001");
  const onWatchRef = useRef(onChange);
  useEffect(() => {
    onWatchRef.current = onChange;
  });
  useEffect(() => {
    if (watchId !== null) {
      onWatchRef.current?.({
        value: watchedValue,
        valueIds: watchedIds === "" ? [] : watchedIds.split("\u0001"),
      });
    }
  }, [watchId, watchedValue, watchedIds]);
}
